<template>
  <v-navigation-drawer
    v-model="drawerLocal"
    :width="drawerWidth"
    app
    left
    :fullscreen="$vuetify.breakpoint.xsAndDown"
  >
    <template v-slot:prepend>
      <v-toolbar flat color="secondary" dark>
        <v-icon>mdi-cart</v-icon>
        <v-toolbar-title class="ml-3"></v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDrawer">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
    </template>

    <v-card flat class="d-flex flex-column cart">
      <v-card-text class="pa-0 flex-grow-1 overflow-y-auto">
        <v-list dense class="cart-list py-0">
          <!-- Empty cart message -->
          <v-list-item v-if="!items.length" class="text-center grey--text">
            <v-list-item-content>
              <v-icon size="64" class="mb-2">mdi-cart-outline</v-icon>
              <div>El carrito está vacío</div>
            </v-list-item-content>
          </v-list-item>

          <!-- Cart items -->
          <template>
            <v-list-item
              v-for="item in items"
              :key="item.ean"
              @click="editItem(item)"
              class="item-row"
            >
              <v-list-item-avatar size="40" class="my-0 mr-3">
                <v-icon>mdi-package-variant-closed</v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="py-1">
                <v-list-item-title class="text-body-1 font-weight-medium">
                  {{ item.nombre || item.marca }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="itemDetail(item)" class="text-caption grey--text">
                  {{ itemDetail(item) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="item.note" class="text--primary text-caption">
                  {{ item.note }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">
                  <span class="mr-2">{{ item.cantidad }} unid</span>
                  <span v-if="item.ean" class="grey--text">{{ item.ean }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <div class="text-right price-column">
                <div class="font-weight-medium">
                  {{ formatMoney(item.precio * item.cantidad) }}
                </div>
                <small
                  v-if="item.discount > 0"
                  class="red--text text-decoration-line-through"
                >
                  {{ formatMoney((item.discount + item.precio) * item.cantidad) }}
                </small>
              </div>
            </v-list-item>

            <v-divider></v-divider>
          </template>

          <!-- Totals -->
          <v-divider v-if="items.length"></v-divider>
          <v-list-item v-if="items.length" class="total-row primary lighten-5">
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold"> Totales </v-list-item-title>
              <v-list-item-subtitle>
                {{ sumField(items, "cantidad").toLocaleString("es") }} artículos
              </v-list-item-subtitle>
            </v-list-item-content>

            <div class="text-right price-column">
              <div class="font-weight-bold text-h6 primary--text">
                {{ formatMoney(totalAmount) }}
              </div>
            </div>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="cart-actions">
        <v-row>
          <v-col cols="6" sm="6">
            <v-text-field
              v-model="bags"
              class="center-input"
              background-color="#FFE0B2"
              label="Bolsas"
              outlined
              dense
              hide-details
              @keyup="updateBags"
            >
              <template v-slot:prepend-inner>
                <v-icon @click="decreaseBags" class="clickable-icon">mdi-minus</v-icon>
              </template>
              <template v-slot:append>
                <v-icon @click="increaseBags" class="clickable-icon">mdi-plus</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="6" sm="6">
            <v-text-field
              v-model="discountValue"
              @keyup="applyDiscount"
              outlined
              dense
              label="Descuento"
              class="center-input"
              background-color="#FFCDD2"
              type="number"
              hide-details
            >
              <template v-slot:append>
                <v-icon>mdi-percent-outline</v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <template v-slot:append class="pa-4">
      <v-toolbar flat color="secondary" dark class="d-flex justify-space-between">
        <v-btn color="success" :disabled="items.length === 0" @click="checkout" block>
          <span class="mr-2">Completar compra</span>
          <span class="font-weight-bold">{{ formatMoney(totalAmount) }}</span>
        </v-btn>
      </v-toolbar>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { formatCurrencyAdvanced } from "@/utils/currencyUtils.js";

export default {
  name: "CartDrawer",
  props: {
    // Control if the drawer is open
    value: {
      type: Boolean,
      default: false,
    },
    // Cart items
    items: {
      type: Array,
      default: () => [],
    },
    // Total amount value
    total: {
      type: Number,
      default: 0,
    },
    // Current number of bags
    bagsCount: {
      type: Number,
      default: 0,
    },
    // Loading state
    loading: {
      type: Boolean,
      default: false,
    },
    // Width of the drawer
    drawerWidth: {
      type: [Number, String],
      default: 400,
    },
    // Overlay color
    overlayColor: {
      type: String,
      default: "#000",
    },
  },
  data() {
    return {
      drawerLocal: this.value,
      discountValue: null,
      bags: this.bagsCount,
    };
  },
  computed: {
    totalAmount() {
      return this.total || this.calculateTotal();
    },
  },
  watch: {
    value(newVal) {
      this.drawerLocal = newVal;
    },
    drawerLocal(newVal) {
      // Emit input event to update v-model in parent
      this.$emit("input", newVal);

      // Also emit open/close events for more semantic event handling
      if (newVal) {
        this.$emit("open");
      } else {
        this.$emit("close");
      }
    },
    bagsCount(newVal) {
      this.bags = newVal;
    },
    bags(newVal) {
      // Using .sync modifier pattern with update: prefix
      this.$emit("update:bags", newVal);
    },
  },
  methods: {
    closeDrawer() {
      this.drawerLocal = false;
    },

    editItem(item) {
      this.$emit("edit-item", item);
    },
    itemDetail(item) {
      if (!item) return "";
      // marca - refe - color - talla, saltando campos vacíos.
      return [item.marca, item.refe, item.color, item.talla]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .join(" - ");
    },
    checkout() {
      this.$emit("checkout");
    },
    decreaseBags() {
      if (this.bags > 0) {
        this.$emit("decreaseBags", true);
      }
    },
    increaseBags() {
      this.$emit("increaseBags", true);
    },
    updateBags() {
      if (this.bags < 0) {
        this.bags = 0;
      }
      this.$emit("updateBags", parseInt(this.bags));
    },
    applyDiscount(event) {
      if (event.keyCode === 13) {
        this.$emit("apply-discount", this.discountValue);
        this.discountValue = null;
      }
    },
    sumField(items, key) {
      return items.reduce((acc, item) => acc + (parseFloat(item[key]) || 0), 0);
    },
    calculateTotal() {
      let total = 0;
      this.items.forEach((item) => {
        total += parseFloat(item.cantidad) * parseFloat(item.precio);
      });
      return total + this.bags * 70; // Adding bag cost (70 per bag)
    },
    formatMoney(value) {
      const n = parseFloat(value);
      if (isNaN(n)) return "";
      return formatCurrencyAdvanced(n, null, { useNativeFormat: true });
    },
  },
};
</script>

<style scoped>
.cart {
  width: 100%;
  height: calc(100vh - 128px);
  display: flex;
  flex-direction: column;
}
.cart-drawer {
  display: flex;
  flex-direction: column;
}

.v-card {
  display: flex;
  flex-direction: column;
}

.v-card-text {
  flex-grow: 1;
  overflow-y: auto;
}

.cart-actions {
  margin-top: auto;
}

.center-input input {
  text-align: center;
}

.cart-list {
  padding: 0;
}

.total-row {
  background-color: #f5f5f5;
}

.price-column {
  min-width: 100px;
  padding-left: 8px;
}

.item-row {
  min-height: 60px;
  padding: 8px 16px;
}

.item-row:hover {
  background-color: #f5f7fa;
}

.clickable-icon {
  cursor: pointer;
}

.clickable-icon:hover {
  opacity: 0.7;
}

.checkout-btn {
  border-radius: 4px;
  text-transform: none;
  letter-spacing: 0;
}
</style>
